import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  BanknoteIcon,
  BellRingIcon,
  CheckIcon,
  CreditCardIcon,
  MinusIcon,
  PlusIcon,
  ReceiptTextIcon,
  ShoppingBagIcon,
  XIcon,
} from '../icons'
import { formatTRY } from '../../utils/money'
import {
  cancelQrRequest,
  fetchQrBill,
  fetchQrStatus,
  sendQrRequest,
  type PayMethod,
  type QrBill,
  type QrRequestState,
  type QrTableInfo,
  type TableRequestKind,
} from '../../services/tableQr'
import type { OrderStrings, ReviewStrings } from './menuI18n'
import { ReviewCard } from './ReviewCard'
import './TableOrder.css'

/**
 * Masadan sipariş (QR Sipariş modülü, 30 Eki 2026). Müşteri masadaki kodu
 * okutunca menünün altına başparmağın yetiştiği yerde bir şerit geliyor:
 * Garson çağır, Hesap iste, sepet doluysa Sepetim. Gönderilen isteğin durumu
 * şeridin hemen üstünde bir kartta: "Garson çağrıldı" -> "Garson geliyor".
 */

export interface TableMode {
  tenantId: string
  token: string
  info: QrTableInfo
}

export interface CartLine {
  key: string
  productId: string
  qty: number
  note?: string
  /** Seçilen porsiyon ("1,5 Porsiyon"); ürünün kendi fiyatıysa yok. */
  portion?: string
}

const lineKey = (productId: string, note?: string, portion?: string) =>
  portion ? `${productId}|${note ?? ''}|${portion}` : `${productId}|${note ?? ''}`

export interface IndexedProduct {
  name: string
  priceKurus: number
  portions?: { name: string; priceKurus: number }[] | null
}

/** Satırın birim fiyatı: porsiyonluysa porsiyonun, yoksa ürünün. */
export const unitPrice = (p: IndexedProduct | undefined, portion?: string) =>
  (portion && p?.portions?.find((x) => x.name === portion)?.priceKurus) || (p?.priceKurus ?? 0)

/** Sepet bu sekmede kalıyor: menüde gezinip sayfayı yenileyen kaybetmesin. */
export function useQrCart(storageKey: string) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]')
      return Array.isArray(raw) ? raw : []
    } catch {
      return []
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(lines))
    } catch {
      // Depolama yoksa sepet yalnız bu açılışta.
    }
  }, [lines, storageKey])

  const add = useCallback((productId: string, note?: string, qty = 1, portion?: string) => {
    const n = note?.trim() || undefined
    const key = lineKey(productId, n, portion)
    setLines((cur) =>
      cur.some((l) => l.key === key)
        ? cur.map((l) => (l.key === key ? { ...l, qty: Math.min(50, l.qty + qty) } : l))
        : [...cur, { key, productId, qty, note: n, ...(portion ? { portion } : {}) }],
    )
  }, [])
  /** Satırdaki eksi: notsuz satırdan, yoksa en son eklenenden bir tane. */
  const less = useCallback((productId: string) => {
    setLines((cur) => {
      const mine = cur.filter((l) => l.productId === productId)
      const pick = mine.find((l) => !l.note) ?? mine[mine.length - 1]
      if (!pick) return cur
      return cur.flatMap((l) => (l.key !== pick.key ? [l] : l.qty > 1 ? [{ ...l, qty: l.qty - 1 }] : []))
    })
  }, [])
  const setQty = useCallback((key: string, qty: number) => {
    setLines((cur) => cur.flatMap((l) => (l.key !== key ? [l] : qty > 0 ? [{ ...l, qty: Math.min(50, qty) }] : [])))
  }, [])
  const clear = useCallback(() => setLines([]), [])
  const qtyOf = useCallback(
    (productId: string) => lines.reduce((n, l) => (l.productId === productId ? n + l.qty : n), 0),
    [lines],
  )
  const count = lines.reduce((n, l) => n + l.qty, 0)
  return { lines, add, less, setQty, clear, qtyOf, count }
}

export type QrCart = ReturnType<typeof useQrCart>

/** Ürün satırındaki ekle düğmesi; sepette varsa "− 2 +". */
export function QrStepper({
  qty,
  name,
  t,
  onAdd,
  onLess,
}: {
  qty: number
  name: string
  t: OrderStrings
  onAdd: () => void
  onLess: () => void
}) {
  return (
    <div
      className={`qo-step ${qty > 0 ? 'is-on' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {qty > 0 && (
        <>
          <button type="button" className="qo-step__btn" onClick={onLess} aria-label={t.less(name)}>
            <MinusIcon />
          </button>
          <b className="qo-step__n" aria-live="polite">
            {qty}
          </b>
        </>
      )}
      <button type="button" className="qo-step__btn qo-step__btn--add" onClick={onAdd} aria-label={t.add(name)}>
        <PlusIcon />
      </button>
    </div>
  )
}

/** Ürün penceresinin altı: hazır notlar, serbest not, adet ve Sepete ekle. */
export function QrSheetAdd({
  priceKurus,
  portions,
  noteOptions,
  t,
  onAdd,
}: {
  priceKurus: number
  /** Porsiyonlu üründe bütün porsiyonlar, ilki ürünün kendi fiyatı (base). */
  portions?: { name: string; priceKurus: number; base?: true }[]
  noteOptions: string[]
  t: OrderStrings
  onAdd: (qty: number, note?: string, portion?: string) => void
}) {
  const [qty, setQty] = useState(1)
  const [picked, setPicked] = useState<string[]>([])
  const [free, setFree] = useState('')
  const [pi, setPi] = useState(0)
  const note = [...picked, free.trim()].filter(Boolean).join(', ')
  const chosen = portions?.[pi]
  const unit = chosen ? chosen.priceKurus : priceKurus

  return (
    <div className="qo-add">
      {/* Porsiyon (3 Kas 2026): adı ve fiyatı, dokununca seçilir. */}
      {portions && portions.length > 1 && (
        <>
          <span className="qo-add__label">{t.portion}</span>
          <div className="qo-portions" role="radiogroup" aria-label={t.portion}>
            {portions.map((x, i) => (
              <button
                key={x.name}
                type="button"
                role="radio"
                aria-checked={i === pi}
                className={`qo-portion ${i === pi ? 'is-on' : ''}`}
                onClick={() => setPi(i)}
              >
                <span className="qo-portion__name">
                  {i === pi && <CheckIcon />}
                  {x.name}
                </span>
                <b>{formatTRY(x.priceKurus)}</b>
              </button>
            ))}
          </div>
        </>
      )}
      <label className="qo-add__label" htmlFor="qo-note">
        {t.notes}
      </label>
      {noteOptions.length > 0 && (
        <div className="qo-add__chips">
          {noteOptions.map((o) => {
            const on = picked.includes(o)
            return (
              <button
                key={o}
                type="button"
                className={`qo-chip ${on ? 'is-on' : ''}`}
                aria-pressed={on}
                onClick={() => setPicked((cur) => (on ? cur.filter((x) => x !== o) : [...cur, o]))}
              >
                {on && <CheckIcon />}
                {o}
              </button>
            )
          })}
        </div>
      )}
      <input
        id="qo-note"
        className="qo-add__note"
        value={free}
        maxLength={80}
        onChange={(e) => setFree(e.target.value)}
        placeholder={t.notePlaceholder}
        autoComplete="off"
      />
      <div className="qo-add__row">
        <div className="qo-qty">
          <button
            type="button"
            className="qo-qty__btn"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            disabled={qty <= 1}
            aria-label="−"
          >
            <MinusIcon />
          </button>
          <b className="qo-qty__n">{qty}</b>
          <button
            type="button"
            className="qo-qty__btn"
            onClick={() => setQty((q) => Math.min(50, q + 1))}
            aria-label="+"
          >
            <PlusIcon />
          </button>
        </div>
        <button type="button" className="qo-primary qo-add__go" onClick={() => onAdd(qty, note || undefined, chosen && !chosen.base ? chosen.name : undefined)}>
          <span>{t.addToCart}</span>
          <span className="qo-primary__sum">{formatTRY(unit * qty)}</span>
        </button>
      </div>
    </div>
  )
}

interface Sent {
  id: string
  kind: TableRequestKind
  at: number
}

const DAY = 12 * 3600_000

/** Bu telefondan gönderilen istekler: durumları yalnız bunlar için soruluyor. */
function useSentRequests(storageKey: string) {
  const [sent, setSent] = useState<Sent[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) ?? '[]') as Sent[]
      return Array.isArray(raw) ? raw.filter((r) => Date.now() - r.at < DAY) : []
    } catch {
      return []
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(sent))
    } catch {
      // Depolama yoksa durum yalnız bu açılışta izleniyor.
    }
  }, [sent, storageKey])
  const remember = useCallback((id: string, kind: TableRequestKind) => {
    setSent((cur) => (cur.some((r) => r.id === id) ? cur : [...cur, { id, kind, at: Date.now() }]))
  }, [])
  const forget = useCallback((id: string) => setSent((cur) => cur.filter((r) => r.id !== id)), [])
  return { sent, remember, forget }
}

/** Sonuçlanmış istek kartı bu kadar sonra kendiliğinden kalkıyor. */
const SHOW_DONE_MS = 3 * 60_000

export function TableDock({
  table,
  t,
  cart,
  products,
  hasDeals,
  review,
}: {
  table: TableMode
  t: OrderStrings
  cart: QrCart
  /** Sepetteki ürünlerin adı ve fiyatı (müşterinin dilinde). */
  products: Map<string, IndexedProduct>
  hasDeals: boolean
  /** Google değerlendirme bağlantısı varsa hesap penceresinde soruluyor. */
  review?: { url: string; t: ReviewStrings }
}) {
  const { tenantId, token } = table
  const base = `adissoo.qr.${tenantId}.${token}`
  const { sent, remember, forget } = useSentRequests(`${base}.sent`)
  const [states, setStates] = useState<Map<string, QrRequestState>>(new Map())
  const [open, setOpen] = useState<'none' | 'cart' | 'bill'>('none')
  const [busy, setBusy] = useState<TableRequestKind | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<number>()

  const say = useCallback((text: string) => {
    setToast(text)
    window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(null), 4000)
  }, [])

  // Durum: bekleyen varken sık, yoksa seyrek; sayfa arkadayken hiç.
  const ids = sent.map((s) => s.id)
  const idKey = ids.join(',')
  const waiting = sent.some((s) => (states.get(s.id)?.status ?? 'pending') === 'pending')
  useEffect(() => {
    if (ids.length === 0) return
    let cancelled = false
    const read = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const list = await fetchQrStatus(tenantId, token, ids)
        if (cancelled) return
        setStates(new Map(list.map((s) => [s.id, s])))
      } catch {
        // Bir sonraki turda yeniden.
      }
    }
    void read()
    const every = window.setInterval(() => void read(), waiting ? 4000 : 15000)
    const onVisible = () => void read()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearInterval(every)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, tenantId, token, waiting])

  /** Kartta gösterilecekler: bekleyenler ve az önce sonuçlananlar. */
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const i = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(i)
  }, [])
  const cards = sent
    .map((s) => ({ ...s, state: states.get(s.id) }))
    .filter(({ state, at }) => {
      const st = state?.status ?? 'pending'
      if (st === 'cancelled') return false
      if (st === 'pending') return true
      return now - (state?.handledAt ?? at) < SHOW_DONE_MS
    })
    .slice(-3)

  const pendingOf = (kind: TableRequestKind) =>
    sent.find((s) => s.kind === kind && (states.get(s.id)?.status ?? 'pending') === 'pending')

  const send = async (kind: TableRequestKind, opts: Parameters<typeof sendQrRequest>[3] = {}) => {
    if (busy) return false
    setBusy(kind)
    try {
      const id = await sendQrRequest(tenantId, token, kind, opts)
      remember(id, kind)
      setStates((cur) => {
        const next = new Map(cur)
        if (!next.has(id)) next.set(id, { id, kind, status: 'pending', createdAt: Date.now() })
        return next
      })
      navigator.vibrate?.(30)
      return true
    } catch (err) {
      say(err instanceof Error && err.message.includes('QR-COK') ? t.tooMany : t.failed)
      return false
    } finally {
      setBusy(null)
    }
  }

  const cancel = async (id: string) => {
    forget(id)
    try {
      await cancelQrRequest(tenantId, token, id)
    } catch {
      // Garson yine görür; müşteriye yalnız kart kalktı.
    }
  }

  const callWaiter = () => {
    if (pendingOf('waiter')) {
      say(t.waiterPending[1])
      return
    }
    void send('waiter')
  }

  const sendOrder = async () => {
    const items = cart.lines.map((l) => ({ productId: l.productId, qty: l.qty, note: l.note, portion: l.portion }))
    if (items.length === 0) return
    if (await send('order', { items })) {
      cart.clear()
      setOpen('none')
    }
  }

  const cartTotal = cart.lines.reduce((n, l) => n + unitPrice(products.get(l.productId), l.portion) * l.qty, 0)

  return (
    <>
      <div className="qo-dock" role="region" aria-label={table.info.name}>
        {toast && (
          <p className="qo-toast" role="status">
            {toast}
          </p>
        )}
        {cards.length > 0 && (
          <ul className="qo-cards" aria-live="polite">
            {cards.map((c) => (
              <StatusCard
                key={c.id}
                kind={c.kind}
                status={c.state?.status ?? 'pending'}
                t={t}
                onCancel={() => void cancel(c.id)}
                onClose={() => forget(c.id)}
              />
            ))}
          </ul>
        )}
        <div className={`qo-bar ${cart.count > 0 ? 'has-cart' : ''}`}>
          <button
            type="button"
            className="qo-act"
            onClick={callWaiter}
            disabled={busy === 'waiter'}
            aria-label={t.callWaiter}
          >
            <BellRingIcon />
            <span className="qo-act__long">{t.callWaiter}</span>
            <span className="qo-act__short">{t.waiterShort}</span>
          </button>
          <button type="button" className="qo-act" onClick={() => setOpen('bill')} aria-label={t.askBill}>
            <ReceiptTextIcon />
            <span className="qo-act__long">{t.askBill}</span>
            <span className="qo-act__short">{t.billShort}</span>
          </button>
          {cart.count > 0 && (
            <button type="button" className="qo-primary qo-bar__cart" onClick={() => setOpen('cart')}>
              <span className="qo-bar__bag">
                <ShoppingBagIcon />
                <b>{cart.count}</b>
              </span>
              <span className="qo-bar__label">{t.cart}</span>
              <span className="qo-primary__sum">{formatTRY(cartTotal)}</span>
            </button>
          )}
        </div>
      </div>

      {open === 'cart' && (
        <Sheet title={t.cart} t={t} onClose={() => setOpen('none')}>
          {cart.lines.length === 0 ? (
            <p className="qo-empty">{t.items(0)}</p>
          ) : (
            <ul className="qo-lines">
              {cart.lines.map((l) => {
                const p = products.get(l.productId)
                return (
                  <li key={l.key} className="qo-line">
                    <div className="qo-line__text">
                      <span className="qo-line__name">{p?.name ?? '?'}</span>
                      {l.portion && <span className="qo-line__note">{l.portion}</span>}
                      {l.note && <span className="qo-line__note">{l.note}</span>}
                    </div>
                    <div className="qo-qty qo-qty--sm">
                      <button
                        type="button"
                        className="qo-qty__btn"
                        onClick={() => cart.setQty(l.key, l.qty - 1)}
                        aria-label={t.less(p?.name ?? '')}
                      >
                        <MinusIcon />
                      </button>
                      <b className="qo-qty__n">{l.qty}</b>
                      <button
                        type="button"
                        className="qo-qty__btn"
                        onClick={() => cart.setQty(l.key, l.qty + 1)}
                        aria-label={t.add(p?.name ?? '')}
                      >
                        <PlusIcon />
                      </button>
                    </div>
                    <b className="qo-line__sum">{formatTRY(unitPrice(p, l.portion) * l.qty)}</b>
                  </li>
                )
              })}
            </ul>
          )}
          <div className="qo-sum">
            <span>{t.total}</span>
            <b>{formatTRY(cartTotal)}</b>
          </div>
          <p className="qo-hint">
            {t.sendHint}
            {hasDeals && ` ${t.dealHint}`}
          </p>
          <button
            type="button"
            className="qo-primary qo-primary--wide"
            onClick={() => void sendOrder()}
            disabled={cart.lines.length === 0 || busy === 'order'}
          >
            {busy === 'order' ? '…' : t.send}
          </button>
        </Sheet>
      )}

      {open === 'bill' && (
        <BillSheet
          table={table}
          t={t}
          pending={!!pendingOf('bill')}
          busy={busy === 'bill'}
          review={review}
          onClose={() => setOpen('none')}
          onAsk={async (pay) => {
            const ok = await send('bill', { pay })
            // Değerlendirme bağlantısı varsa pencere "Hesap istendi" ekranıyla açık kalıyor.
            if (ok && !review) setOpen('none')
            return ok
          }}
        />
      )}
    </>
  )
}

function StatusCard({
  kind,
  status,
  t,
  onCancel,
  onClose,
}: {
  kind: TableRequestKind
  status: QrRequestState['status']
  t: OrderStrings
  onCancel: () => void
  onClose: () => void
}) {
  const [title, sub] =
    kind === 'waiter'
      ? status === 'pending'
        ? t.waiterPending
        : t.waiterSeen
      : kind === 'bill'
        ? status === 'pending'
          ? t.billPending
          : t.billSeen
        : status === 'pending'
          ? t.orderPending
          : status === 'rejected'
            ? t.orderRejected
            : t.orderAccepted
  const tone = status === 'pending' ? 'wait' : status === 'rejected' ? 'bad' : 'ok'
  return (
    <li className={`qo-card qo-card--${tone}`}>
      <span className="qo-card__icon" aria-hidden="true">
        {status === 'pending' ? (
          kind === 'order' ? <ShoppingBagIcon /> : kind === 'bill' ? <ReceiptTextIcon /> : <BellRingIcon />
        ) : status === 'rejected' ? (
          <XIcon />
        ) : (
          <CheckIcon />
        )}
      </span>
      <span className="qo-card__text">
        <b>{title}</b>
        <span>{sub}</span>
      </span>
      {status === 'pending' ? (
        <button type="button" className="qo-card__cancel" onClick={onCancel}>
          {t.cancel}
        </button>
      ) : (
        <button type="button" className="qo-x qo-x--sm" onClick={onClose} aria-label={t.close}>
          <XIcon />
        </button>
      )}
    </li>
  )
}

/** Alttan açılan pencere: başlık, kırmızı çarpı, içerik. */
function Sheet({
  title,
  t,
  onClose,
  children,
}: {
  title: string
  t: OrderStrings
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])
  return (
    <div className="pm__sheet-bg" onClick={onClose}>
      <div
        className="pm__sheet qo-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="qo-sheet__head">
          <h3>{title}</h3>
          <button type="button" className="qo-x" onClick={onClose} aria-label={t.close}>
            <XIcon />
          </button>
        </header>
        <div className="qo-sheet__body">{children}</div>
      </div>
    </div>
  )
}

function BillSheet({
  table,
  t,
  pending,
  busy,
  review,
  onClose,
  onAsk,
}: {
  table: TableMode
  t: OrderStrings
  pending: boolean
  busy: boolean
  review?: { url: string; t: ReviewStrings }
  onClose: () => void
  onAsk: (pay: PayMethod) => Promise<boolean>
}) {
  const [bill, setBill] = useState<QrBill | null>(null)
  const [done, setDone] = useState(false)
  const [failed, setFailed] = useState(false)
  const [pay, setPay] = useState<PayMethod>('card')
  useEffect(() => {
    let cancelled = false
    fetchQrBill(table.tenantId, table.token)
      .then((b) => !cancelled && setBill(b))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [table.tenantId, table.token])

  const remaining = bill ? Math.max(0, bill.totalKurus - bill.prepaidKurus) : 0
  const empty = bill !== null && bill.lines.length === 0

  if (done && review) {
    return (
      <Sheet title={t.bill} t={t} onClose={onClose}>
        <div className="qo-done">
          <span className="qo-done__icon" aria-hidden="true">
            <CheckIcon />
          </span>
          <b>{t.billPending[0]}</b>
          <span className="qo-done__sub">{t.billPending[1]}</span>
        </div>
        <ReviewCard url={review.url} t={review.t} />
      </Sheet>
    )
  }

  return (
    <Sheet title={t.bill} t={t} onClose={onClose}>
      {!bill && !failed && (
        <div className="qo-skel" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      )}
      {failed && <p className="qo-empty">{t.failed}</p>}
      {empty && <p className="qo-empty">{t.billEmpty}</p>}
      {bill && !empty && (
        <>
          <ul className="qo-lines qo-lines--bill">
            {bill.lines.map((l) => (
              <li key={l.name} className="qo-line">
                <span className="qo-line__qty">{l.qty}×</span>
                <span className="qo-line__name">{l.name}</span>
                <b className="qo-line__sum">{formatTRY(l.totalKurus)}</b>
              </li>
            ))}
          </ul>
          <div className="qo-sum">
            <span>{t.total}</span>
            <b>{formatTRY(bill.totalKurus)}</b>
          </div>
          {bill.prepaidKurus > 0 && (
            <>
              <div className="qo-sum qo-sum--sub">
                <span>{t.paid}</span>
                <b>−{formatTRY(bill.prepaidKurus)}</b>
              </div>
              <div className="qo-sum">
                <span>{t.remaining}</span>
                <b>{formatTRY(remaining)}</b>
              </div>
            </>
          )}
          <p className="qo-add__label">{t.payHow}</p>
          <div className="qo-pay" role="radiogroup" aria-label={t.payHow}>
            {(
              [
                ['card', t.card, <CreditCardIcon key="c" />],
                ['cash', t.cash, <BanknoteIcon key="n" />],
              ] as const
            ).map(([key, label, icon]) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={pay === key}
                className={`qo-pay__opt ${pay === key ? 'is-on' : ''}`}
                onClick={() => setPay(key)}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="qo-primary qo-primary--wide"
            onClick={async () => {
              if (await onAsk(pay)) setDone(true)
            }}
            disabled={busy}
          >
            {pending ? t.billPending[0] : t.requestBill}
          </button>
          {pending && review && <ReviewCard url={review.url} t={review.t} />}
        </>
      )}
    </Sheet>
  )
}

/** Sepetteki ürünlerin adı ve fiyatı, müşterinin dilinde. */
export function useProductIndex(
  categories: { products: { id: string; name: string; priceKurus: number; portions?: IndexedProduct['portions'] }[] }[],
) {
  return useMemo(() => {
    const map = new Map<string, IndexedProduct>()
    for (const c of categories)
      for (const p of c.products) map.set(p.id, { name: p.name, priceKurus: p.priceKurus, portions: p.portions })
    return map
  }, [categories])
}
